import { Controller, useWatch } from "react-hook-form";

import {
  Description,
  Error,
  FormItem,
  Input,
  InputLabel,
  InputLengthCounter,
  Required,
} from "./styles";
import { MdError } from "react-icons/md";
import { useIntl } from "react-intl";
import { useToast } from "../../hooks/toast";
import sanitizeHtml from "sanitize-html";

const ONLY_DIGITS_REGEX = /\D/g;

const getErrorByPath = (errors, path) => {
  if (!errors || !path) return undefined;

  const keys = String(path)
    .split(".")
    .map((key) => {
      const asNumber = Number(key);
      return Number.isNaN(asNumber) ? key : asNumber;
    });

  return keys.reduce((acc, key) => (acc ? acc[key] : undefined), errors);
};

export default function ControlledInput({
  name,
  control,
  defaultValue,
  type = "text",
  disabled = false,
  onChange,
  min,
  max,
  placeholder,
  step,
  label,
  required,
  description,
  formState,
  arrayName,
  creator,
  maxLength,
  ...props
}) {
  const intl = useIntl();
  const { addToast } = useToast();

  const errorMessage =
    // 1) path (funciona para ranges.0.positionStart)
    getErrorByPath(formState?.errors, name)?.message ||
    // 2) fallback antigo (mantém compatibilidade)
    formState?.errors?.[name]?.message ||
    formState?.errors?.[arrayName]?.[name.split(".")[1]]?.[name.split(".")[2]]
      ?.message;

  const labelIntl = label
    ? intl.formatMessage({
        id: label,
      })
    : null;
  const watch = useWatch({ control, name });

  const isRequired = label && required && <Required>*</Required>;
  const hasDesc = description && (
    <Description>
      (
      {intl.formatMessage({
        id: description,
      })}
      )
    </Description>
  );

  const isNumeric = type === "number";

  const applyMaxLength = (value) => {
    if (!maxLength) return value;
    return value.length > maxLength ? value.slice(0, maxLength) : value;
  };

  const normalizeValue = (value) => {
    const stringValue = String(value ?? "");
    const normalized = isNumeric
      ? stringValue.replace(ONLY_DIGITS_REGEX, "")
      : stringValue;

    return applyMaxLength(normalized);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormItem width={props.width}>
          {label && (
            <InputLabel htmlFor={name} creator={creator}>
              {labelIntl}
              {isRequired} {hasDesc}
            </InputLabel>
          )}
          <Input
            {...field}
            {...props}
            id={name}
            type={isNumeric ? "text" : type}
            inputMode={isNumeric ? "numeric" : props.inputMode}
            pattern={isNumeric ? "[0-9]*" : props.pattern}
            disabled={disabled}
            min={min}
            max={max}
            maxLength={maxLength}
            step={step}
            placeholder={placeholder}
            error={errorMessage}
            onPaste={(e) => {
              if (!max) return; // Let default paste behavior handle it

              const pastedText = e.clipboardData.getData("text");
              const selectionStart = e.target.selectionStart;
              const selectionEnd = e.target.selectionEnd;
              const currentText = field.value || "";

              // Calculate final length considering selection
              const finalLength =
                currentText.length -
                (selectionEnd - selectionStart) +
                pastedText.length;

              if (finalLength > max) {
                e.preventDefault();
                return addToast({
                  type: "error",
                  title: intl.formatMessage(
                    {
                      id: "error.paste_max_length",
                    },
                    { max, current: finalLength }
                  ),
                });
              }
              // Let default paste behavior handle it if within limits
            }}
            onChange={(e) => {
              if (onChange) return onChange(e);

              if (max) {
                const newValue = e.target.value;
                const cleanValue = sanitizeHtml(newValue, {
                  allowedTags: [],
                  allowedAttributes: {},
                });

                if (cleanValue.length > max) {
                  return;
                }
              }
              field.onChange(e);
            }}
            onBlur={(e) => {
              field.onBlur();
              if (props.onBlur) props.onBlur(e);
            }}
          />
          <ErrorMessage errorMessage={errorMessage} />
          {max && (
            <InputLengthCounter>
              {watch ? String(watch).replace(/<[^>]*>/g, "").length : 0} / {max}{" "}
              {intl.formatMessage({ id: "characters" })}
            </InputLengthCounter>
          )}
        </FormItem>
      )}
    />
  );
}

export function ErrorMessage({ errorMessage }) {
  const intl = useIntl();

  if (!errorMessage) return null;

  const getErrorMessage = () => {
    if (!errorMessage) return;
    switch (true) {
      case errorMessage.includes("error.max_length"):
      case errorMessage.includes("error.min_length"): {
        const splited = errorMessage.split("-");
        const id = splited[0];
        const length = splited[1];
        return intl.formatMessage(
          {
            id,
          },
          {
            length,
          }
        );
      }
      case errorMessage.includes("invalid_workload"): {
        return intl.formatMessage({
          id: "invalid_workload",
        });
      }
      case errorMessage.includes("informa_academy_invalid_lesson_video_link"): {
        return intl.formatMessage({
          id: "informa_academy_invalid_lesson_video_link",
        });
      }
      case errorMessage.includes("error.valid_url"): {
        return intl.formatMessage({
          id: "error.valid_url",
        });
      }
      case errorMessage.includes("error.need_to_be_decimal"): {
        return intl.formatMessage({
          id: "error.need_to_be_decimal",
        });
      }
      case errorMessage.includes("error.need_to_be_number"):
      case errorMessage.includes("Expected number"): {
        return intl.formatMessage({
          id: "error.need_to_be_number",
        });
      }
      default: {
        return intl.formatMessage({
          id: "error.field_required",
        });
      }
    }
  };

  return (
    <Error>
      <MdError color="#DC2626" size={14} />
      {getErrorMessage(errorMessage)}
    </Error>
  );
}
